import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BsModalRef } from 'ngx-bootstrap';

const untilDestroyedSymbol = Symbol('untilDestroyed');

const bsModalUntilDestroyedSymbol = Symbol('bsModalUntilDestroyed');
/**
 * RxJS operator that unsubscribe from observables on destory.
 * Code forked from https://github.com/NetanelBasal/ngx-take-until-destroy
 *
 * IMPORTANT: Add the `untilDestroyed` operator as the last one to prevent leaks with intermediate observables in the
 * operator chain.
 *
 * @param instance The parent Angular component or object instance.
 * @param destroyMethodName The method to hook on (default: 'ngOnDestroy').
 * @example
 * ```
 * import { untilDestroyed } from '@app/core';
 *
 * @Component({
 * selector: 'app-example',
 *   templateUrl: './example.component.html'
 * })
 * export class ExampleComponent implements OnInit, OnDestroy {
 *   ngOnInit() {
 *     interval(1000)
 *       .pipe(untilDestroyed(this))
 *       .subscribe(val => console.log(val));
 *   }
 *
 *   // This method must be present, even if empty.
 *   ngOnDestroy() {
 *     // To protect you, an error will be thrown if it doesn't exist.
 *   }
 * }
 * ```
 */
export function untilDestroyed(instance: object, destroyMethodName: string = 'ngOnDestroy') {
  return <T>(source: Observable<T>) => {
    const originalDestroy = instance[destroyMethodName];
    const hasDestroyFunction = typeof originalDestroy === 'function';

    if (!hasDestroyFunction) {
      throw new Error(
        `${instance.constructor.name} is using untilDestroyed but doesn't implement ${destroyMethodName}`
      );
    }

    if (!instance[untilDestroyedSymbol]) {
      instance[untilDestroyedSymbol] = new Subject();

      instance[destroyMethodName] = function () {
        if (hasDestroyFunction) {
          originalDestroy.apply(this, arguments);
        }
        instance[untilDestroyedSymbol].next();
        instance[untilDestroyedSymbol].complete();
      };
    }

    return source.pipe(takeUntil<T>(instance[untilDestroyedSymbol]));
  };
}

/**
 * é¡µé¢Destroyæ¶bsModalå¼¹çªèªå¨å³é­
 *
 *
 * IMPORTANT: Add the `bsModalUntilDestroyed` function to hide bsModal when destroyed
 *
 * @param instance The parent Angular component or object instance.
 * @param remove if true remove bsModalRef else add
 * @param destroyMethodName The method to hook on (default: 'ngOnDestroy').
 * @example
 * ```
 *
 * @Component({
 * selector: 'app-example',
 *   templateUrl: './example.component.html'
 * })
 * export class ExampleComponent implements OnInit, OnDestroy {
 *
 *   createForm() {
 *     const bsModalRef = this.modalService.show(CreateFormComponent, { ignoreBackdropClick: true });
 *     bsModalUntilDestroyed(this, bsModalRef);
 *   }
 *
 *   // This method must be present, even if empty.
 *   ngOnDestroy() {
 *     // To protect you, an error will be thrown if it doesn't exist.
 *   }
 * }
 * ```
 */
export function bsModalUntilDestroyed(
  instance: object,
  bsModalRef: BsModalRef,
  remove: boolean = false,
  destroyMethodName: string = 'ngOnDestroy'
) {
  const originalDestroy = instance[destroyMethodName];
  const hasDestroyFunction = typeof originalDestroy === 'function';

  if (!hasDestroyFunction) {
    throw new Error(
      `${instance.constructor.name} is using bsModalUntilDestroyed but doesn't implement ${destroyMethodName}`
    );
  }

  if (!instance[bsModalUntilDestroyedSymbol]) {
    instance[bsModalUntilDestroyedSymbol] = [];

    instance[destroyMethodName] = function () {
      if (hasDestroyFunction) {
        originalDestroy.apply(this, arguments);
      }
      (instance[bsModalUntilDestroyedSymbol] as BsModalRef[]).forEach((s) => {
        if (s) {
          s.hide();
        }
      });
    };
  }

  const bsModalRefs = instance[bsModalUntilDestroyedSymbol] as BsModalRef[];
  if (remove) {
    bsModalRefs.splice(bsModalRefs.indexOf(bsModalRef), 1);
  } else {
    bsModalRefs.push(bsModalRef);
  }
}
